/**
 * sampleImport — on-demand demo Wine collection so a first-run user
 * can see CDI populated without hand-entering a cellar.
 *
 * Client-only. Uses the existing REST endpoints:
 *   POST /collections    → new collection (type_id="wine")
 *   POST /categories     → new category (auto-seeds Wine master template)
 *   POST /items          → each demo wine
 * and tags the created collection id in
 *   localStorage.cdi:sample_data_collection_id
 * so the whole app (badges, migration, cleanup, merge suggestions) can
 * short-circuit around it — the sample cellar is inert demo data, not
 * a real user cellar.
 */
import { apiClient } from "@/lib/api";

export const SAMPLE_COLLECTION_KEY = "cdi:sample_data_collection_id";

const wine = (item_name, cost, value, notes) => ({
  item_name,
  cost_price_ex_gst: cost,
  estimated_value_ex_gst: value,
  currency_code: "NZD",
  ...notes,
});

/**
 * Declarative manifest for the demo cellar. Ten bottles across all
 * five wine styles (Red / White / Sparkling / Rosé / Fortified) with
 * realistic producers, vintages, regions, grapes, ABVs, and price
 * ladders so Dashboard KPIs paint a believable portfolio the moment
 * the user taps "Import".
 *
 * `item_data` keys map 1-for-1 to the Wine master template shipped
 * from server.py so the migration helper leaves these items alone.
 */
export const SAMPLE_MANIFEST = {
  collection: {
    name: "Sample Wine Collection",
    description: "A demo cellar showcasing CDI's wine analytics — safe to delete anytime.",
    type_id: "wine",
    currency: "NZD",
    currency_symbol: "$",
  },
  category: {
    category_name: "Sample Cellar",
    category_description: "Curated demo wines across every major style.",
  },
  items: [
    wine("Château Margaux 2015", 1250.00, 1650.00, {
      item_data: {
        "Producer": "Château Margaux",
        "Item Name": "Château Margaux",
        "Vintage": "2015",
        "Country": "France",
        "Region": "Bordeaux",
        "Sub-Region": "Médoc",
        "Appellation": "Margaux AOC",
        "Type": "Red",
        "Grape(s)": "Cabernet Sauvignon, Merlot, Cabernet Franc, Petit Verdot",
        "Bottle Size": "750ml",
        "ABV": "13.5%",
        "Classification": "Premier Grand Cru Classé",
        "Drink From": "2025",
        "Drink To": "2050",
        "Tasting Notes": "Ripe blackcurrant, violets, graphite, silky tannins. 98/100.",
        "Personal Notes": "Cellared for the 40th.",
        "Image": "",
        "Storage Location": "Cellar Rack A1",
        "Purchase Date": "2018-06-14",
        "Purchase Source": "Bordeaux En Primeur",
      },
    }),
    wine("Penfolds Grange 2010", 950.00, 1400.00, {
      item_data: {
        "Producer": "Penfolds", "Item Name": "Grange", "Vintage": "2010",
        "Country": "Australia", "Region": "South Australia", "Sub-Region": "Barossa Valley",
        "Appellation": "", "Type": "Red",
        "Grape(s)": "Shiraz", "Bottle Size": "750ml", "ABV": "14.5%",
        "Classification": "Icon", "Drink From": "2020", "Drink To": "2045",
        "Tasting Notes": "Dark plum, mocha, black pepper, long finish. 96/100.",
        "Personal Notes": "Anniversary bottle.", "Image": "",
        "Storage Location": "Cellar Rack A2",
        "Purchase Date": "2015-11-02", "Purchase Source": "Penfolds Cellar Door",
      },
    }),
    wine("Barolo Riserva 2012 — Giacomo Conterno", 480.00, 720.00, {
      item_data: {
        "Producer": "Giacomo Conterno", "Item Name": "Monfortino Riserva",
        "Vintage": "2012", "Country": "Italy", "Region": "Piedmont",
        "Sub-Region": "Serralunga d'Alba", "Appellation": "Barolo DOCG",
        "Type": "Red", "Grape(s)": "Nebbiolo", "Bottle Size": "750ml",
        "ABV": "14.0%", "Classification": "Riserva",
        "Drink From": "2028", "Drink To": "2055",
        "Tasting Notes": "Rose, tar, dried cherry, chalky tannins. 97/100.",
        "Personal Notes": "", "Image": "", "Storage Location": "Cellar Rack B1",
        "Purchase Date": "2017-04-21", "Purchase Source": "Auction — Zachys",
      },
    }),
    wine("Cloudy Bay Sauvignon Blanc 2022", 42.00, 55.00, {
      item_data: {
        "Producer": "Cloudy Bay", "Item Name": "Sauvignon Blanc", "Vintage": "2022",
        "Country": "New Zealand", "Region": "Marlborough", "Sub-Region": "Wairau Valley",
        "Appellation": "", "Type": "White",
        "Grape(s)": "Sauvignon Blanc", "Bottle Size": "750ml", "ABV": "13.0%",
        "Classification": "", "Drink From": "2023", "Drink To": "2026",
        "Tasting Notes": "Passionfruit, cut grass, zesty grapefruit. 92/100.",
        "Personal Notes": "House pour with oysters.", "Image": "",
        "Storage Location": "Fridge Bay 1",
        "Purchase Date": "2023-01-18", "Purchase Source": "Glengarry",
      },
    }),
    wine("Chablis Grand Cru Les Clos 2020 — William Fèvre", 165.00, 220.00, {
      item_data: {
        "Producer": "William Fèvre", "Item Name": "Les Clos Grand Cru",
        "Vintage": "2020", "Country": "France", "Region": "Burgundy",
        "Sub-Region": "Chablis", "Appellation": "Chablis Grand Cru",
        "Type": "White", "Grape(s)": "Chardonnay", "Bottle Size": "750ml",
        "ABV": "13.5%", "Classification": "Grand Cru",
        "Drink From": "2024", "Drink To": "2035",
        "Tasting Notes": "Flint, oyster shell, lemon curd, precise finish. 95/100.",
        "Personal Notes": "", "Image": "", "Storage Location": "Cellar Rack C1",
        "Purchase Date": "2022-09-05", "Purchase Source": "Caro's",
      },
    }),
    wine("Dom Pérignon 2013", 320.00, 410.00, {
      item_data: {
        "Producer": "Moët & Chandon", "Item Name": "Dom Pérignon", "Vintage": "2013",
        "Country": "France", "Region": "Champagne", "Sub-Region": "",
        "Appellation": "Champagne AOC", "Type": "Sparkling",
        "Grape(s)": "Chardonnay, Pinot Noir", "Bottle Size": "750ml",
        "ABV": "12.5%", "Classification": "Vintage",
        "Drink From": "2023", "Drink To": "2040",
        "Tasting Notes": "Brioche, white peach, fine bead, saline finish. 96/100.",
        "Personal Notes": "For the next milestone.", "Image": "",
        "Storage Location": "Cellar Rack D1",
        "Purchase Date": "2022-12-10", "Purchase Source": "Farro Fresh",
      },
    }),
    wine("Bollinger Special Cuvée NV", 95.00, 120.00, {
      item_data: {
        "Producer": "Bollinger", "Item Name": "Special Cuvée", "Vintage": "NV",
        "Country": "France", "Region": "Champagne", "Sub-Region": "",
        "Appellation": "Champagne AOC", "Type": "Sparkling",
        "Grape(s)": "Pinot Noir, Chardonnay, Pinot Meunier", "Bottle Size": "750ml",
        "ABV": "12.0%", "Classification": "Non-Vintage",
        "Drink From": "2023", "Drink To": "2028",
        "Tasting Notes": "Toasted almond, ripe apple, creamy mousse. 91/100.",
        "Personal Notes": "", "Image": "", "Storage Location": "Cellar Rack D2",
        "Purchase Date": "2023-03-22", "Purchase Source": "Glengarry",
      },
    }),
    wine("Whispering Angel Rosé 2022", 38.00, 48.00, {
      item_data: {
        "Producer": "Château d'Esclans", "Item Name": "Whispering Angel",
        "Vintage": "2022", "Country": "France", "Region": "Provence",
        "Sub-Region": "Côtes de Provence", "Appellation": "Côtes de Provence AOC",
        "Type": "Rosé",
        "Grape(s)": "Grenache, Cinsault, Rolle, Tibouren, Syrah",
        "Bottle Size": "750ml", "ABV": "13.0%", "Classification": "",
        "Drink From": "2023", "Drink To": "2025",
        "Tasting Notes": "Pale coral, wild strawberry, citrus zest, dry finish. 90/100.",
        "Personal Notes": "Summer BBQ default.", "Image": "",
        "Storage Location": "Fridge Bay 2",
        "Purchase Date": "2023-11-30", "Purchase Source": "Moore Wilson's",
      },
    }),
    wine("Domaine Ott Rosé 2021 — Château de Selle", 62.00, 78.00, {
      item_data: {
        "Producer": "Domaine Ott", "Item Name": "Château de Selle Rosé",
        "Vintage": "2021", "Country": "France", "Region": "Provence",
        "Sub-Region": "Côtes de Provence", "Appellation": "Côtes de Provence AOC",
        "Type": "Rosé", "Grape(s)": "Grenache, Cinsault, Syrah",
        "Bottle Size": "750ml", "ABV": "13.0%", "Classification": "",
        "Drink From": "2022", "Drink To": "2025",
        "Tasting Notes": "Peach blossom, red berries, mineral finish. 93/100.",
        "Personal Notes": "", "Image": "", "Storage Location": "Fridge Bay 3",
        "Purchase Date": "2022-08-14", "Purchase Source": "Fine Wine Delivery Co.",
      },
    }),
    wine("Taylor's 20 Year Tawny Port", 88.00, 115.00, {
      item_data: {
        "Producer": "Taylor Fladgate", "Item Name": "20 Year Tawny",
        "Vintage": "NV", "Country": "Portugal", "Region": "Douro",
        "Sub-Region": "", "Appellation": "Porto DOC", "Type": "Fortified",
        "Grape(s)": "Touriga Nacional, Tinta Roriz, Touriga Franca",
        "Bottle Size": "750ml", "ABV": "20.0%", "Classification": "Tawny 20 Year",
        "Drink From": "2023", "Drink To": "2035",
        "Tasting Notes": "Walnut, caramel, dried fig, long spirit-warm finish. 94/100.",
        "Personal Notes": "Post-dinner staple.", "Image": "",
        "Storage Location": "Cellar Rack E1",
        "Purchase Date": "2023-05-08", "Purchase Source": "Caro's",
      },
    }),
  ],
};

/** Read the client-side sample-collection id (null if not yet imported). */
export function getSampleCollectionId() {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(SAMPLE_COLLECTION_KEY) || null; }
  catch { return null; }
}
export function hasSampleData() { return !!getSampleCollectionId(); }
function setSampleCollectionId(id) {
  if (typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(SAMPLE_COLLECTION_KEY, id);
    else window.localStorage.removeItem(SAMPLE_COLLECTION_KEY);
  } catch { /* quota — best effort */ }
}

/**
 * Verify the sample collection referenced by localStorage still exists
 * server-side. If the user deleted it from the Collections page, we
 * clear the flag so the Import button re-enables automatically.
 */
export async function reconcileSampleCollection() {
  const id = getSampleCollectionId();
  if (!id) return null;
  try {
    await apiClient.get(`/collections/${id}`);
    return id;
  } catch (e) {
    // 404 → the user deleted the sample from the Collections page.
    if (e?.response?.status === 404) {
      setSampleCollectionId(null);
      return null;
    }
    return id; // treat transient errors as "still exists" so we don't wipe the flag
  }
}

/**
 * Idempotent seeder. Refuses to run when a sample already exists so a
 * double-tap can't create parallel demo cellars. Returns
 * `{ collectionId, itemCount, alreadyExists }`.
 */
export async function importSampleData() {
  const existing = await reconcileSampleCollection();
  if (existing) {
    return { collectionId: existing, itemCount: 0, alreadyExists: true };
  }
  // 1) Collection
  const colRes = await apiClient.post("/collections", SAMPLE_MANIFEST.collection);
  const collection = colRes.data;
  // 2) Category (auto-seeds the Wine master template server-side).
  const catRes = await apiClient.post("/categories", {
    ...SAMPLE_MANIFEST.category,
    collection_id: collection.id,
  });
  const category = catRes.data;
  // 3) Items — sequential to preserve created_at ordering for a natural
  // dashboard reveal.
  let created = 0;
  for (const it of SAMPLE_MANIFEST.items) {
    try {
      await apiClient.post("/items", {
        image: "",
        item_name: it.item_name,
        collection_id: collection.id,
        category_id: category.id,
        cost_price_ex_gst: it.cost_price_ex_gst,
        estimated_value_ex_gst: it.estimated_value_ex_gst,
        currency_code: it.currency_code,
        item_data: it.item_data,
      });
      created++;
    } catch { /* skip failed items — dashboard still meaningful with partials */ }
  }
  setSampleCollectionId(collection.id);
  return { collectionId: collection.id, itemCount: created, alreadyExists: false };
}

/**
 * Cascade DELETE the sample collection (backend already deletes its
 * categories + items) and clear the flag.
 */
export async function removeSampleData() {
  const id = getSampleCollectionId();
  if (!id) return { removed: false };
  try {
    await apiClient.delete(`/collections/${id}`);
  } catch (e) {
    // 404 → already gone; still clear the flag so UI stays consistent.
    if (e?.response?.status !== 404) throw e;
  }
  setSampleCollectionId(null);
  return { removed: true };
}

/** True if the given collection id matches the sample collection. */
export function isSampleCollectionId(id) {
  const sampleId = getSampleCollectionId();
  return !!sampleId && sampleId === id;
}
